import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../apiBase";

const API = API_BASE;

const PARTICIPANTS = [
    { key: "Dealer", label: "Dealers / Intermediary", desc: "Sell-side banks, dealers, swap desks." },
    { key: "Asset_Mgr", label: "AM : Asset Managers / Institutional", desc: "Pensions, insurers, mutual funds, institutional PMs." },
    { key: "Lev_Money", label: "LM : Leveraged Funds", desc: "Hedge funds, CTAs." },
    { key: "Other_Rept", label: "OthRept : Other Reportables", desc: "Other large reportable participants not assigned to the first three. Non-dealer hedgers with balance-sheet or business-risk motivations, often structurally short index (or long downside convexity) when equity valuations/vol premia justify it." },
    { key: "NonRept", label: "NonRept : Non Reportables", desc: "Smaller participants, number of contracts doesn't reach the level to file a daily reporting. Smaller hedge funds, Individual Investors (likely more to tactical positioning, less structural hedge)." },
];

const SUB_HEADERS = {
    "Treasury Bonds": [
        { after: 0, label: "U.S. Treasury bonds and notes (classic & ultra)" },
        { after: 6, label: "Micro yield / curve contracts" },
        { after: 7, label: "Eris SOFR swap futures" },
    ],
    "Equities": [
        { after: 0, label: "S&P 500 and related" },
        { after: 7, label: "S&P sector indices" },
        { after: 15, label: "Dow / DJIA family" },
        { after: 19, label: "Nasdaq family" },
        { after: 22, label: "Russell family" },
        { after: 26, label: "Global and regional equity indices" },
        { after: 30, label: "Volatility index" },
    ],
    "Currencies (FX)": [
        { after: 0, label: "Major FX futures" },
        { after: 7, label: "Emerging-market FX" },
        { after: 10, label: "FX cross-rates and indices" },
    ],
    "Crypto": [
        { after: 0, label: "Bitcoin (all styles)" },
        { after: 4, label: "Ether (all styles)" },
        { after: 8, label: "Major altcoins (non-perp and perp)" },
        { after: 15, label: "Other perp-style contracts (Coinbase/LMX suite)" },
    ],
};

export default function ContractGuide() {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCat, setOpenCat] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/cot-pro/contracts`)
            .then((r) => { setContracts(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const categories = [...new Set(contracts.map((c) => c.category))];
    const toggle = (cat) => setOpenCat(openCat === cat ? null : cat);

    if (loading) return <div style={{ color: "var(--pord-muted)", padding: "40px 0" }}>Loading...</div>;

    return (
        <div data-testid="contract-guide" style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 32, alignItems: "start" }}>
            {/* LEFT — Contract Directory */}
            <div>
                <h2 className="pord-heading" style={{ fontSize: 20, color: "#fff", marginBottom: 16 }}>Contract Directory</h2>
                {categories.map((cat, catIdx) => {
                    const items = contracts.filter((c) => c.category === cat);
                    const isOpen = openCat === cat;
                    const subs = SUB_HEADERS[cat] || [];
                    return (
                        <div key={cat} style={{ marginBottom: 2 }}>
                            <button data-testid={`cat-toggle-${cat}`} onClick={() => toggle(cat)} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                width: "100%", textAlign: "left", padding: "9px 14px",
                                background: "rgba(154,173,168,0.12)",
                                border: "none", borderRadius: 0, cursor: "pointer",
                            }}>
                                <span style={{ fontSize: 15, color: "var(--pord-teal)", fontWeight: 500 }}>{catIdx + 1}. {cat}</span>
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ fontSize: 11, color: "var(--pord-muted)" }}>{items.length}</span>
                                    <span style={{ color: "var(--pord-sage)", fontSize: 12, transition: "transform 200ms", transform: isOpen ? "rotate(180deg)" : "" }}>▾</span>
                                </span>
                            </button>
                            {isOpen && (
                                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: 40 }} />
                                        <col />
                                        <col style={{ width: 70 }} />
                                        <col style={{ width: 55 }} />
                                        <col style={{ width: "22%" }} />
                                        <col style={{ width: "22%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid rgba(154,173,168,0.12)" }}>
                                            <th style={{ padding: "8px 6px 8px 14px", width: 32, fontSize: 13, color: "var(--pord-muted)", textAlign: "right" }}>#</th>
                                            <th style={{ padding: "8px 8px", fontSize: 13, color: "var(--pord-muted)", textAlign: "left" }}>Name</th>
                                            <th style={{ padding: "8px 8px", width: 60, fontSize: 13, color: "var(--pord-muted)", textAlign: "left" }}>Code</th>
                                            <th style={{ padding: "8px 8px", fontSize: 13, color: "rgba(218,242,208,0.5)", textAlign: "left" }}>Proxy ETF</th>
                                            <th style={{ padding: "8px 8px", fontSize: 13, color: "var(--pord-muted)", textAlign: "left" }}>Contract Size</th>
                                            <th style={{ padding: "8px 8px", fontSize: 13, color: "var(--pord-muted)", textAlign: "left" }}>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((c, i) => {
                                            const sub = subs.find((s) => s.after === i);
                                            return (
                                                <React.Fragment key={c.code}>
                                                    {sub && (
                                                        <tr>
                                                            <td colSpan={6} style={{ padding: i === 0 ? "10px 14px 6px" : "18px 14px 6px", fontSize: 15, fontWeight: 500, fontStyle: "italic", color: "rgba(255,255,255,0.7)", borderTop: i === 0 ? "none" : "1px solid rgba(154,173,168,0.10)" }}>
                                                                {sub.label}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    <tr data-testid={`contract-${c.code}`} style={{ borderBottom: "1px solid rgba(184,212,208,0.04)" }}>
                                                        <td style={{ padding: "8px 6px 8px 14px", width: 32, fontSize: 15, color: "var(--pord-teal)", textAlign: "right", fontVariantNumeric: "tabular-nums", verticalAlign: "top" }}>{c.num}</td>
                                                        <td style={{ padding: "8px 8px", fontSize: 15, color: "rgba(255,255,255,0.9)", fontWeight: 400, verticalAlign: "top" }}>
                                                            {c.name}
                                                            <div style={{ fontSize: 11, color: "rgba(154,173,168,0.4)", marginTop: 2 }}>{c.exchange}</div>
                                                        </td>
                                                        <td style={{ padding: "8px 8px", width: 60, fontSize: 14, color: "rgba(140,195,160,0.85)", fontVariantNumeric: "tabular-nums", textAlign: "left", verticalAlign: "top" }}>{c.code}</td>
                                                        <td style={{ padding: "8px 8px", width: 50, fontSize: 14, color: c.etf_proxy ? "rgba(218,242,208,0.7)" : "rgba(255,255,255,0.15)", fontWeight: 500, verticalAlign: "top" }}>{c.etf_proxy || "—"}</td>
                                                        <td style={{ padding: "8px 8px", fontSize: 14, color: "#fff", verticalAlign: "top" }}>{c.size || "—"}</td>
                                                        <td style={{ padding: "8px 8px", fontSize: 14, color: "#DAF2D0", verticalAlign: "top", fontStyle: c.notes ? "italic" : "normal" }}>{c.notes || ""}</td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* RIGHT — How to Use + Participants */}
            <div style={{ position: "sticky", top: 24 }}>
                <section style={{ marginBottom: 20 }}>
                    <h2 className="pord-heading" style={{ fontSize: 18, color: "#fff", marginBottom: 12 }}>How to Use This Dashboard</h2>
                    <div style={{
                        padding: "20px 22px", borderRadius: 10,
                        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
                    }}>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.75, margin: "0 0 12px" }}>
                            The <strong style={{ color: "#fff" }}>Weekly Summary</strong> shows all contracts at a glance.
                            Look for <strong style={{ color: "rgba(210,150,135,0.95)" }}>Extreme</strong> readings for crowding or divergence signals.
                        </p>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.75, margin: "0 0 12px" }}>
                            The <strong style={{ color: "#fff" }}>Dashboard</strong> provides deep-dive dual-axis charts with percentile bands.
                        </p>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.75, margin: 0 }}>
                            Percentile calculations use 52-week lookback matching Excel's PERCENTRANK.INC.
                        </p>
                    </div>
                </section>
                <section>
                    <h2 className="pord-heading" style={{ fontSize: 18, color: "#fff", marginBottom: 12 }}>TFF Reports : Traders in Financial Futures ; Futures-and-Options Combined Reports</h2>
                    <div style={{ display: "grid", gap: 10 }}>
                        {PARTICIPANTS.map(({ key, label, desc }) => (
                            <div key={key} data-testid={`participant-${key}`} style={{
                                padding: "16px 20px",
                                background: "rgba(255,255,255,0.025)",
                                border: "1px solid rgba(255,255,255,0.06)",
                                borderRadius: 10,
                            }}>
                                <div style={{ fontSize: 15, color: "#fff", fontWeight: 500, marginBottom: 6 }}>
                                    {label}
                                </div>
                                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.65 }}>
                                    {desc}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
